import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Form, Modal, PageWrap, Spinner, Table } from '@vwfs-bronson/bronson-react';
import { Formik } from 'formik';
import { NumericFormField } from '../../components/FormFields/NumericFormField';
import { calculateQuote, getQuoteVehicleOptions } from '../../services/api/miles/miles-services';
import { transformDateFormatWithSlash } from '../../services/common/format';
import { roundNumber } from '../../services/common/formatNew';
import { store } from '../../services/redux';
import { loadingActions, loadingSelectors } from '../../services/redux/features/loading.slice';
import { useNavigate } from '../../services/routing/navigate';
import { calculateTotalAccessoriesPrice } from '../FormPage/Step1/initial-values-financial';
import { totalCost } from '../utils';
import { AccessoriesCostPopupModalSchema } from './validationSchema';

interface AccessoriesModalProps {
  visible: boolean;
  resetPopupValues: boolean;
  setResetPopupValues: (val) => void;
  onCancel: (val?) => void;
  onConfirm: (val?) => void;
  estDelivery?: string;
  catalogPrice?: number;
  quoteId?: string;
  prefillOptions?: Array<QuoteSchema.Schemas.VehicleOptionGetQuote>;
  isReadonly?: boolean;
}

type InitialValuesType = {
  priceBeforeTax5: number;
  priceBeforeTax20: number;
  priceWithTax5: number;
  priceWithTax20: number;
  totalCost5?: number;
  totalCost20?: number;
};

const vatCode20PercentId: string = '100639';
const tax20Percent: number = 1.2;
const tax5Comma5Percent: number = 1.055;

export const AccessoriesModal: FunctionComponent<AccessoriesModalProps> = (props) => {
  const formRef: any = useRef();
  const { t } = useTranslation();
  const {
    visible,
    onCancel,
    onConfirm,
    resetPopupValues,
    setResetPopupValues,
    estDelivery,
    catalogPrice,
    quoteId,
    prefillOptions,
    isReadonly,
  } = props;
  const [vatCode5Comma5PercentId, setVatCode5Comma5PercentId] = useState<string>('');

  const navigate = useNavigate();
  const isLoading = useSelector(loadingSelectors.selectLoading);

  const getEnumIdByTaxCode = (
    vehicleOptions: VehicleIntegration.Components.Schemas.ModelOptions[],
    taxCode: string
  ): string => {
    const option = vehicleOptions.find((opt: any) => opt.optionPriceAmount.taxCode === taxCode);
    return option?.optionPriceAmount?.catalogVatCode?.enumId ?? '';
  };

  const getOptionPrice = (key: string): number =>
    Array.isArray(prefillOptions)
      ? (prefillOptions.find((o) => o.prKey === key)?.optionPriceAmount?.netAmount ?? 0)
      : 0;

  let initialValues: InitialValuesType = {
    priceBeforeTax5: 0,
    priceBeforeTax20: 0,
    priceWithTax5: 0,
    priceWithTax20: 0,
    totalCost5: 0,
    totalCost20: 0,
  };

  if (Array.isArray(prefillOptions) && prefillOptions.length > 0) {
    const priceBeforeTax5 = getOptionPrice('400002');
    const priceBeforeTax20 = getOptionPrice('400001');
    const priceWithTax5 = roundNumber(priceBeforeTax5 * tax5Comma5Percent);
    const priceWithTax20 = roundNumber(priceBeforeTax20 * tax20Percent);

    initialValues = {
      priceBeforeTax5,
      priceBeforeTax20,
      priceWithTax5,
      priceWithTax20,
      totalCost5: priceBeforeTax5 + priceBeforeTax20,
      totalCost20: priceWithTax5 + priceWithTax20,
    };
  }

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (!quoteId) {
      throw new Error('Missing quote id!');
    }

    const getRequiredEnumIdsOfAccessories = async () => {
      try {
        store.dispatch(loadingActions.setLoading(true));

        const vehicleOptionsResponse = await getQuoteVehicleOptions(quoteId);
        const vehicleDataOptions = vehicleOptionsResponse.vehicleData?.model?.options;

        if (vehicleDataOptions) {
          const enumIdFranceInputVATInland5Comma5: string = getEnumIdByTaxCode(
            vehicleDataOptions,
            'FranceInputVATInland5Comma5'
          );

          setVatCode5Comma5PercentId(enumIdFranceInputVATInland5Comma5);
        }
      } catch {
        navigate.error();
      } finally {
        store.dispatch(loadingActions.setLoading(false));
      }
    };

    getRequiredEnumIdsOfAccessories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, visible]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSubmit = async (values) => {
    /* mock */
  };

  const [savedValues, setSavedValues] = useState<InitialValuesType>(initialValues);
  const [confirmationDisabled, setConfirmationDisabled] = useState<boolean>(true);

  const calculateAndSendAccessoriesCosts = async (): Promise<Array<QuoteSchema.Schemas.VehicleOptionGetQuote>> => {
    store.dispatch(loadingActions.setLoading(true));
    const request: QuoteSchema.Schemas.CalculateSalesQuoteRequest = {
      ...(estDelivery && {
        financialProduct: {
          contracts: [
            {
              deliveryData: {
                deliveryDate: transformDateFormatWithSlash(estDelivery),
              },
            },
          ],
        },
      }),
      vehicleData: {
        model: {
          options: [
            {
              prTypeId: '400002',
              isSelected: true,
              optionPriceAmount: {
                amount: formRef.current.values.priceBeforeTax5,
                catalogVatCode: {
                  enumId: vatCode5Comma5PercentId,
                },
              },
            },
            {
              prTypeId: '400001',
              isSelected: true,
              optionPriceAmount: {
                amount: formRef.current.values.priceBeforeTax20,
                catalogVatCode: {
                  enumId: vatCode20PercentId,
                },
              },
            },
          ],
        },
        ...(catalogPrice && {
          milesDealerVehiclePrice: {
            grossAmount: catalogPrice,
          },
        }),
      },
    };
    try {
      if (!quoteId) {
        throw new Error('Missing quote id!');
      }
      const response = await calculateQuote(quoteId, request);

      return response.vehicleData.model?.options ?? [];
    } catch {
      navigate.error();
    } finally {
      store.dispatch(loadingActions.setLoading(false));
    }

    return [];
  };

  return (
    <Modal
      id="accessoriesOptions"
      title={t('accessoriesOptions:title')}
      buttonConfirmText={isReadonly ? null : t('accessoriesOptions:apply')} // Only show confirm button if not read-only
      buttonCancelText={t('accessoriesOptions:cancel')}
      shown={visible}
      confirmationDisabled={confirmationDisabled}
      onClose={() => onCancel('close')}
      onCancel={() => {
        onCancel('close');
        formRef.current?.resetForm({ values: savedValues });
      }}
      onConfirm={async () => {
        const currentValues = formRef.current.values;
        setSavedValues(currentValues);
        const options = await calculateAndSendAccessoriesCosts();
        const totalPrice = calculateTotalAccessoriesPrice(options);
        onConfirm(totalPrice);
      }}
      asPortal
      className="c-modal--large"
    >
      <Formik
        initialValues={initialValues}
        innerRef={formRef}
        onSubmit={onSubmit}
        enableReinitialize
        validationSchema={AccessoriesCostPopupModalSchema()}
      >
        {(formProps) => {
          const { values, handleSubmit, setFieldValue, errors } = formProps;
          const handleSubmitOwn = (e) => {
            handleSubmit(e);
          };
          const isValid: boolean = Object.keys(errors).length === 0;
          setConfirmationDisabled(!isValid);

          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            setFieldValue('totalCost5', totalCost(values.priceBeforeTax5, values.priceBeforeTax20));
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [values.priceBeforeTax5, values.priceBeforeTax20]);

          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            setFieldValue('totalCost20', totalCost(values.priceWithTax5, values.priceWithTax20));
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [values.priceWithTax5, values.priceWithTax20]);

          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            if (resetPopupValues) {
              formRef.current?.resetForm({ values: initialValues });
              setSavedValues(initialValues);
              setResetPopupValues(false);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [resetPopupValues]);
          return (
            <Form onSubmit={handleSubmitOwn}>
              <PageWrap>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th> </Table.Th>
                      <Table.Th>{t('accessoriesOptions:tva')}</Table.Th>
                      <Table.Th>{t('accessoriesOptions:priceBeforeTax')}</Table.Th>
                      <Table.Th>{t('accessoriesOptions:priceAfterTax')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td>{t('accessoriesOptions:accReducedVat')}</Table.Td>
                      <Table.Td>{t('accessoriesOptions:fivePercentage')}</Table.Td>
                      <Table.Td>
                        <NumericFormField
                          fieldName="priceBeforeTax5"
                          type="money"
                          maximumFractionDigits={2}
                          isDisabled={isReadonly}
                          onValueChange={(value) =>
                            setFieldValue('priceWithTax5', roundNumber(value * tax5Comma5Percent))
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <NumericFormField
                          fieldName="priceWithTax5"
                          type="money"
                          maximumFractionDigits={2}
                          isDisabled={isReadonly}
                          onValueChange={(value) =>
                            setFieldValue('priceBeforeTax5', roundNumber(value / tax5Comma5Percent))
                          }
                        />
                      </Table.Td>
                    </Table.Tr>

                    <Table.Tr>
                      <Table.Td>{t('accessoriesOptions:accWithVat')}</Table.Td>
                      <Table.Td>{t('accessoriesOptions:twentyPercentage')}</Table.Td>
                      <Table.Td>
                        <NumericFormField
                          fieldName="priceBeforeTax20"
                          type="money"
                          maximumFractionDigits={2}
                          isDisabled={isReadonly}
                          onValueChange={(value) => setFieldValue('priceWithTax20', roundNumber(value * tax20Percent))}
                        />
                      </Table.Td>
                      <Table.Td>
                        <NumericFormField
                          fieldName="priceWithTax20"
                          type="money"
                          maximumFractionDigits={2}
                          isDisabled={isReadonly}
                          onValueChange={(value) =>
                            setFieldValue('priceBeforeTax20', roundNumber(value / tax20Percent))
                          }
                        />
                      </Table.Td>
                    </Table.Tr>

                    <Table.Tr>
                      <Table.Td>{t('accessoriesOptions:total')}</Table.Td>
                      <Table.Td />
                      <Table.Td>
                        <NumericFormField
                          fieldName="totalCost5"
                          type="money"
                          maximumFractionDigits={2}
                          isReadonly={!isReadonly}
                          isDisabled={isReadonly}
                        />
                      </Table.Td>
                      <Table.Td>
                        <NumericFormField
                          fieldName="totalCost20"
                          type="money"
                          maximumFractionDigits={2}
                          isReadonly={!isReadonly}
                          isDisabled={isReadonly}
                        />
                      </Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </PageWrap>
            </Form>
          );
        }}
      </Formik>
      {/* TODO Remove when Bronson fixes BRONSD-1042 */}
      {isLoading && <Spinner fullPage />}
    </Modal>
  );
};
